import type { SetTimeRequest } from '@festina/life-general-types/life-test'
import {
  useSubmitCollCalcTime,
  useSubmitDatamart2Time,
  useSubmitDatamartTime,
  useSubmitLegalEntityTime,
  useSubmitLifeTime,
  useSubmitMoneyInTime,
  useSubmitMoneyOutTime,
  useSubmitUnitLinkTime,
} from '@festina-life/api'
import {
  dateUtils,
  notEmpty,
  type Optional,
  ServersWithTestService,
  type ServerWithTest,
  type ServerWithTestWithoutReplicators,
} from '@festina-life/core'
import { Button, ButtonContainer } from '@festina-life/ui/actions'
import { DatePicker } from '@festina-life/ui/forms'
import { useTranslate } from '@festina-life/ui/hooks'
import { CardItem, CardItems, SimpleModal } from '@festina-life/ui/structure'
import dayjs from 'dayjs'
import groupBy from 'lodash-es/groupBy'
import { useState } from 'react'

import { useAvailableServers } from '../../hooks/servers/useAvailableServers'

type SystemTime = {
  name: string
  time: Optional<string>
}

type Props = {
  availableTimes: SystemTime[]
  onClose: () => void
  shouldAllowToChangeTime: boolean
}

export const TimeModal = ({
  availableTimes,
  onClose,
  shouldAllowToChangeTime,
}: Props) => {
  const translate = useTranslate()
  const { mutateAsync: lifeTrigger } = useSubmitLifeTime()
  const { mutateAsync: legalEntityTrigger } = useSubmitLegalEntityTime()
  const { mutateAsync: moneyOutTrigger } = useSubmitMoneyOutTime()
  const { mutateAsync: unitLinkTrigger } = useSubmitUnitLinkTime()
  const { mutateAsync: collCalcTrigger } = useSubmitCollCalcTime()
  const { mutateAsync: moneyInTrigger } = useSubmitMoneyInTime()
  const { mutateAsync: datamartTrigger } = useSubmitDatamartTime()
  const { mutateAsync: datamart2Trigger } = useSubmitDatamart2Time()

  const triggersForServers: Record<
    ServerWithTestWithoutReplicators,
    typeof lifeTrigger
  > = {
    life: lifeTrigger,
    legalEntity: legalEntityTrigger,
    moneyOut: moneyOutTrigger,
    moneyIn: moneyInTrigger,
    unitLink: unitLinkTrigger,
    collCalc: collCalcTrigger,
    dataMart: datamartTrigger,
    dataMart2: datamart2Trigger,
  }

  const availableServers = useAvailableServers()
  const availableServersSet = new Set(availableServers)
  const isAvailable = (
    server: ServerWithTest,
  ): server is ServerWithTestWithoutReplicators =>
    server !== 'lifeDataReplicator' &&
    server !== 'unitLinkDataReplicator' &&
    server !== 'legalEntityDataReplicator' &&
    availableServersSet.has(server)

  const availableTriggers = ServersWithTestService.reduce<
    (typeof lifeTrigger)[]
  >(
    (acc, server) =>
      isAvailable(server) ? [...acc, triggersForServers[server]] : acc,
    [],
  )

  const [isSubmitting, setIsSubmitting] = useState(false)

  const justTimes = availableTimes.map(({ time }) => time).filter(notEmpty)

  const groupedTimes = groupBy(justTimes, (t) => t)

  // Find the group with the most elements
  const mostCommonDate = Object.entries(groupedTimes).reduce(
    (acc, [date, times]) =>
      times.length > acc.times ? { date, times: times.length } : acc,
    { date: '', times: 0 },
  ).date

  const [newTime, setNewTime] = useState<Optional<string>>(
    dateUtils.dateAsDateTimeStr(mostCommonDate),
  )

  const handleSubmit = async (newTime: Optional<string>) => {
    if (!newTime) {
      return
    }
    setIsSubmitting(true)

    const requestBody = {
      requestBody: {
        _type: 'SetTimeRequest',
        dateTime: dayjs.utc(newTime).toISOString().slice(0, -1), // Remove the Z
      } satisfies SetTimeRequest,
    }

    const results = availableTriggers.map((trigger) => trigger(requestBody))

    await Promise.all(results)
    setIsSubmitting(false)
    onClose()
  }

  const formId = 'timeForm'

  return (
    <SimpleModal
      title={translate('timeModal.title')}
      isOpen
      closeConfiguration={{ onClose, label: translate('close') }}
      acceptConfiguration={
        shouldAllowToChangeTime
          ? {
              label: translate('timeModal.setTime'),
              formId,
              loading: isSubmitting,
              onAccept: () => handleSubmit(newTime),
            }
          : undefined
      }
      className="min-w-[300px]"
    >
      <CardItems>
        {availableTimes.map(({ name, time }) => (
          <CardItem
            key={name}
            title={name}
            value={dateUtils.dateWithTimeAsStr(time)}
          />
        ))}
      </CardItems>

      {shouldAllowToChangeTime && (
        <>
          <DatePicker
            value={newTime ?? undefined}
            onChange={setNewTime}
            name="newTime"
            wrapperProps={{ label: 'Set time' }}
            granularity="minute"
          />
          <ButtonContainer className="justify-end">
            <Button
              onClick={() => {
                setNewTime(
                  dateUtils.dateAsServerStr(
                    dayjs(newTime).add(1, 'day').toISOString(),
                  ),
                )
              }}
            >
              {translate('timeModal.addDay')}
            </Button>
            <Button
              onClick={() => {
                setNewTime(
                  dateUtils.dateAsServerStr(
                    dayjs(newTime).add(1, 'month').toISOString(),
                  ),
                )
              }}
            >
              {translate('timeModal.addMonth')}
            </Button>
            <Button
              onClick={() => {
                setNewTime(
                  dateUtils.dateAsServerStr(
                    dayjs(newTime).add(1, 'year').toISOString(),
                  ),
                )
              }}
            >
              {translate('timeModal.addYear')}
            </Button>
          </ButtonContainer>
        </>
      )}
    </SimpleModal>
  )
}
